import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import * as Slider from "@radix-ui/react-slider";
import { RevealOnScroll, formatCurrency } from "../shared";
import { ROI_THESIS, SLIDER_FIELDS, OUTPUT_METRICS } from "./roiCalculatorContent";

const SLIDER_LIMITS = {
  clients: { min: 1, max: 100, step: 1 },
  markup: { min: 150, max: 400, step: 10 },
  churn: { min: 5, max: 30, step: 1 },
};

const SliderField = ({ label, value, display, limits, onChange, testId }) => (
  <div className="mb-7 last:mb-0" data-testid={testId}>
    <div className="flex justify-between items-baseline gap-4 mb-3">
      <label className="text-sm font-medium text-partner">{label}</label>
      <span className="text-base font-mono font-bold text-partner-accent tabular-nums shrink-0">
        {display}
      </span>
    </div>
    <Slider.Root
      className="relative flex items-center select-none touch-none w-full h-5"
      value={[value]}
      min={limits.min}
      max={limits.max}
      step={limits.step}
      onValueChange={([v]) => onChange(v)}
      aria-label={label}
    >
      <Slider.Track className="bg-[#080A0F] relative grow rounded-full h-2 border border-partner">
        <Slider.Range className="absolute bg-[#00D2FF] rounded-full h-full" />
      </Slider.Track>
      <Slider.Thumb className="block w-5 h-5 bg-[#E6EFFA] border-2 border-[#00D2FF] rounded-full focus:outline-none focus:ring-2 focus:ring-[#00D2FF]/25" />
    </Slider.Root>
  </div>
);

const RoiCalculatorSection = ({
  id = "roi-calculator",
  hideHeader = false,
  hideChurnSlider = false,
  defaultClients = 15,
  defaultMarkup = 300,
  footnote = "",
  introLine = "",
}) => {
  const [clients, setClients] = useState(defaultClients);
  const [markup, setMarkup] = useState(defaultMarkup);
  const [churnReduction, setChurnReduction] = useState(15);

  const { mrrGrowth, annualProfit, ltvMultiplier } = useMemo(() => {
    const mrr = clients * markup;
    return {
      mrrGrowth: mrr,
      annualProfit: mrr * 12,
      ltvMultiplier: Math.min(2.5, 1 + churnReduction / 10),
    };
  }, [clients, markup, churnReduction]);

  const ltvDisplay =
    ltvMultiplier % 1 === 0 ? `${ltvMultiplier}x` : `${ltvMultiplier.toFixed(1)}x`;

  const sliderState = {
    clients: { value: clients, display: String(clients), set: setClients },
    markup: { value: markup, display: formatCurrency(markup), set: setMarkup },
    churn: { value: churnReduction, display: `${churnReduction}%`, set: setChurnReduction },
  };

  const visibleSliderFields = SLIDER_FIELDS.filter((field) => !(hideChurnSlider && field.id === "churn"));
  const visibleOutputMetrics = OUTPUT_METRICS.filter((metric) => !(hideChurnSlider && metric.id === "ltv"));

  const secondaryValues = {
    annual: `+${formatCurrency(annualProfit)}`,
    ltv: `Up to ${ltvDisplay}`,
  };

  return (
    <section
      id={id}
      className={`section-padding relative overflow-hidden ${hideHeader ? "pt-0 pb-0" : "section-alt"}`}
      data-testid="roi-calculator"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {!hideHeader && (
          <RevealOnScroll className="text-center mb-10">
            {introLine && (
              <p className="text-partner-muted font-body leading-relaxed max-w-2xl mx-auto mb-6">{introLine}</p>
            )}
            <span className="badge-mono text-partner-accent bg-[#00D2FF]/10 border border-[#00D2FF]/25 px-3 py-1.5 rounded-full inline-block mb-5">
              {ROI_THESIS.badge}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3">
              {ROI_THESIS.headline.split("Unutilized Margin")[0]}
              <span className="gradient-text">Unutilized Margin</span>
            </h2>
            <p className="text-partner-muted font-body leading-relaxed">{ROI_THESIS.subhead}</p>
          </RevealOnScroll>
        )}

        <RevealOnScroll delay={hideHeader ? 0 : 0.1}>
          <div className="bento-card border border-partner overflow-hidden" data-testid="roi-calculator-card">
            {hideHeader && (
              <div className="px-6 sm:px-8 pt-8 pb-2 text-center border-b border-partner">
                <span className="badge-mono text-partner-accent bg-[#00D2FF]/10 border border-[#00D2FF]/25 px-3 py-1.5 rounded-full inline-block mb-3">
                  ROI CALCULATOR
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-partner leading-tight mb-2">
                  Calculate Your <span className="gradient-text">Unutilized Margin</span>
                </h3>
                <p className="text-sm text-partner-muted font-body">
                  Adjust your client book to see how much recurring cash you are leaving on the table today.
                </p>
              </div>
            )}
            <div className="px-6 sm:px-8 py-8 sm:py-10 text-center border-b border-partner bg-[#00D2FF]/5">
              <p className="text-sm text-partner-muted font-body mb-2">
                {clients} active clients × {formatCurrency(markup)} markup
              </p>
              <motion.p
                key={mrrGrowth}
                initial={{ opacity: 0.6 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="font-mono text-4xl sm:text-5xl font-bold text-partner-accent tabular-nums"
                data-testid="roi-hero-result"
              >
                +{formatCurrency(mrrGrowth)}
                <span className="text-xl sm:text-2xl text-partner-muted font-normal">/mo</span>
              </motion.p>
              <p className="text-sm font-medium text-partner mt-2">Immediate Net MRR Growth</p>
              {footnote && (
                <p className="text-xs font-mono text-partner-muted mt-2">{footnote}</p>
              )}
            </div>

            <div className="px-6 sm:px-8 py-8">
              {visibleSliderFields.map((field) => {
                const state = sliderState[field.id];
                return (
                  <SliderField
                    key={field.id}
                    label={field.label}
                    value={state.value}
                    display={state.display}
                    limits={SLIDER_LIMITS[field.id]}
                    onChange={state.set}
                    testId={field.testId}
                  />
                );
              })}
            </div>

            <div
              className={`grid border-t border-partner ${
                visibleOutputMetrics.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
              }`}
            >
              {visibleOutputMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className="px-6 py-5 sm:py-6 text-center sm:first:border-r border-partner border-b sm:border-b-0 last:border-b-0"
                  data-testid={`readout-${metric.id}`}
                >
                  <p className="font-mono text-2xl font-bold text-partner tabular-nums">
                    {secondaryValues[metric.id]}
                    {metric.suffix && (
                      <span className="text-base text-partner-muted font-normal">{metric.suffix}</span>
                    )}
                  </p>
                  <p className="text-sm text-partner-muted mt-1">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

export default RoiCalculatorSection;
